/**
 * Native Android Storage Utility using Capacitor Preferences
 * Optimized for Android APK builds - uses native storage
 */

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const isNative = Capacitor.isNativePlatform();

// For Android native app, always use Capacitor Preferences
// Only fallback to localStorage if not in native (development/testing)
function getStorageAPI() {
  if (isNative) {
    return Preferences;
  }
  // Development fallback only
  return null;
}

/**
 * Get a value from storage
 * @param {string} key - Storage key
 * @returns {Promise<string|null>} Stored value or null
 */
export async function getStorageItem(key) {
  try {
    const storage = getStorageAPI();
    if (storage) {
      const { value } = await storage.get({ key });
      return value;
    }
    // Development fallback
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Error getting storage item ${key}:`, error);
    // Ultimate fallback for development
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.error('Storage failed:', e);
      return null;
    }
  }
}

/**
 * Set a value in storage
 * @param {string} key - Storage key
 * @param {string} value - Value to store
 * @returns {Promise<void>}
 */
export async function setStorageItem(key, value) {
  try {
    const storage = getStorageAPI();
    if (storage) {
      await storage.set({ key, value });
      return;
    }
    // Development fallback
    localStorage.setItem(key, value);
  } catch (error) {
    console.error(`Error setting storage item ${key}:`, error);
    // Ultimate fallback for development
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.error('Storage failed:', e);
    }
  }
}

/**
 * Remove a value from storage
 * @param {string} key - Storage key
 * @returns {Promise<void>}
 */
export async function removeStorageItem(key) {
  try {
    const storage = getStorageAPI();
    if (storage) {
      await storage.remove({ key });
      return;
    }
    // Development fallback
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing storage item ${key}:`, error);
    // Ultimate fallback for development
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error('Storage failed:', e);
    }
  }
}

/**
 * Clear all storage
 * @returns {Promise<void>}
 */
export async function clearStorage() {
  try {
    const storage = getStorageAPI();
    if (storage) {
      await storage.clear();
      return;
    }
    // Development fallback
    localStorage.clear();
  } catch (error) {
    console.error('Error clearing storage:', error);
    // Ultimate fallback for development
    try {
      localStorage.clear();
    } catch (e) {
      console.error('Storage failed:', e);
    }
  }
}

/**
 * Get all keys from storage
 * @returns {Promise<string[]>} Array of keys
 */
export async function getStorageKeys() {
  try {
    const storage = getStorageAPI();
    if (storage) {
      const { keys } = await storage.keys();
      return keys;
    }
    // Development fallback
    return Object.keys(localStorage);
  } catch (error) {
    console.error('Error getting storage keys:', error);
    // Ultimate fallback for development
    try {
      return Object.keys(localStorage);
    } catch (e) {
      console.error('Storage failed:', e);
      return [];
    }
  }
}

/**
 * Get JSON object from storage
 * @param {string} key - Storage key
 * @returns {Promise<Object|null>} Parsed JSON object or null
 */
export async function getStorageJSON(key) {
  const value = await getStorageItem(key);
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error(`Error parsing JSON for key ${key}:`, error);
    return null;
  }
}

/**
 * Set JSON object in storage
 * @param {string} key - Storage key
 * @param {Object} value - Object to store
 * @returns {Promise<void>}
 */
export async function setStorageJSON(key, value) {
  try {
    const jsonString = JSON.stringify(value);
    await setStorageItem(key, jsonString);
  } catch (error) {
    console.error(`Error storing JSON for key ${key}:`, error);
  }
}

